import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore, type User } from '@/stores/auth';
import { apiClient } from '@/lib/api';

/**
 * OAuth callback response from the backend
 */
interface OAuthCallbackResponse {
  user: User;
}

/**
 * Main authentication hook with OAuth actions
 *
 * Provides:
 * - Current authentication state (user, isAuthenticated, isLoading)
 * - login() - Initiates Google OAuth flow (alias for loginWithGoogle)
 * - loginWithGoogle() - Initiates Google OAuth flow
 * - handleOAuthCallback() - Processes OAuth callback code
 * - logout() - Logs out the current user
 *
 * Authentication is managed via HttpOnly cookies set by the backend.
 * No token management is done on the frontend for security.
 */
export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    setUser,
    logout: storeLogout,
    setLoading,
    isInitialized,
    setInitialized,
    isHydrated,
  } = useAuthStore();

  /**
   * Initialize auth state on mount - validate session with backend
   * Only runs after localStorage rehydration completes to prevent race conditions
   */
  useEffect(() => {
    let cancelled = false;

    const initializeAuth = async () => {
      // Wait for rehydration to complete before initializing
      if (!isHydrated || isInitialized || cancelled) return;

      // Skip initialization if we're on the OAuth callback page
      // The callback handler will set the user state directly
      if (
        window.location.pathname === '/auth/callback' ||
        window.location.pathname === '/auth/discord/callback'
      ) {
        return;
      }

      try {
        // Check if we have a valid session via cookies
        const userData = await apiClient.get<User>('/auth/me');
        if (cancelled) return;

        setUser(userData);
      } catch {
        // No valid session - clear auth state
        if (!cancelled) {
          storeLogout();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
    };
  }, [isHydrated, isInitialized, setInitialized, setLoading, setUser, storeLogout]);

  /**
   * Re-validate session when tab becomes visible after being in background
   * Uses lastFetchedAt to avoid unnecessary checks (min 5 minutes interval)
   */
  useEffect(() => {
    const REVALIDATE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!isInitialized) return;

      const { lastFetchedAt } = useAuthStore.getState();
      const now = Date.now();
      if (lastFetchedAt && now - lastFetchedAt < REVALIDATE_THRESHOLD_MS) return;

      try {
        const userData = await apiClient.get<User>('/auth/me');
        setUser(userData);
      } catch {
        storeLogout();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isInitialized, setUser, storeLogout]);

  /**
   * Initiates Google OAuth login by redirecting to the OAuth endpoint
   * Backend handles CSRF protection via state parameter stored in KV
   */
  const loginWithGoogle = useCallback(() => {
    // Use VITE_API_URL to redirect to backend OAuth endpoint
    // In production, this goes to the Workers API domain
    const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
    window.location.href = `${apiUrl}/auth/google`;
  }, []);

  /**
   * Initiates Discord OAuth login by redirecting to the OAuth endpoint
   * Backend handles CSRF protection via state parameter stored in KV
   */
  const loginWithDiscord = useCallback(() => {
    const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
    window.location.href = `${apiUrl}/auth/discord`;
  }, []);

  /**
   * Alias for loginWithGoogle - Initiates Google OAuth login
   */
  const login = loginWithGoogle;

  /**
   * Handles the OAuth callback by exchanging the auth code for a session
   * Backend validates the state parameter via KV storage for CSRF protection
   * The backend sets an HttpOnly cookie upon successful authentication
   * @param code - The authorization code from the OAuth provider
   * @param state - The state parameter (validated by backend against KV)
   * @throws Error if the code exchange fails
   */
  const handleOAuthCallback = useCallback(
    async (code: string, state: string): Promise<void> => {
      // Mark as initialized immediately to prevent initializeAuth from running
      // in other components during callback processing
      setInitialized(true);
      setLoading(true);

      // Call backend callback endpoint with code and state as query params
      // Backend validates state against its KV storage for CSRF protection
      // Use URLSearchParams for safe parameter encoding
      const params = new URLSearchParams({ code, state });
      const response = await apiClient.get<OAuthCallbackResponse>(
        `/auth/google/callback?${params.toString()}`
      );
      setUser(response.user);
      setLoading(false);
    },
    [setUser, setInitialized, setLoading]
  );

  /**
   * Logs out the current user
   * Clears the HttpOnly cookie on the backend and local state
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Log error for debugging but continue with logout
      console.error('Logout API call failed (continuing anyway):', error);
    } finally {
      storeLogout();
    }
  }, [storeLogout]);

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithGoogle,
    loginWithDiscord,
    handleOAuthCallback,
    logout,
  };
}

/**
 * Simple hook to get the current user
 *
 * Returns null if not authenticated, otherwise returns the User object
 */
export function useUser(): User | null {
  return useAuthStore((state) => state.user);
}

/**
 * Authentication guard hook that redirects to login if not authenticated
 *
 * Use this hook in protected routes/components to ensure the user is authenticated.
 * If not authenticated and not loading, redirects to /login with the current path as redirect.
 *
 * @returns Object with user, isAuthenticated, and isLoading state
 */
export function useRequireAuth(): {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
} {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const isRedirecting = useRef(false);

  useEffect(() => {
    // Prevent redirect if component unmounts or already redirecting
    let isMounted = true;

    // Only redirect after initial loading is complete and user is not authenticated
    if (!isLoading && !isAuthenticated && isMounted && !isRedirecting.current) {
      isRedirecting.current = true;
      const currentPath = window.location.pathname;
      const redirectUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
      window.location.href = redirectUrl;
    }

    return () => {
      isMounted = false;
    };
  }, [isLoading, isAuthenticated]);

  return { user, isAuthenticated, isLoading };
}

/**
 * Alias for useRequireAuth - Authentication guard hook
 * Use this hook in protected routes/components to ensure the user is authenticated.
 */
export const useAuthGuard = useRequireAuth;

// Re-export User type for convenience
export type { User } from '@/stores/auth';
